import React, { useMemo, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Typography,
  Tab,
  Tabs,
  Avatar,
  Grid,
  Modal,
  Slide,
  Button,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Menu,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import copy from "copy-to-clipboard";

import { Alert } from "../../common/styled";
import { useNotification } from "../../../../contexts/NotificationContext";

import ReactPerfectScrollbar from "react-perfect-scrollbar";
import { stringAvatar } from "../../../../layouts/_new/Normal";
import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import { generateMenuSx } from "../../../../layouts/_new/Normal";
import { ONE_DAY_IN_MS } from "../../../../utils/constants";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as PersonalIcon } from "../../../../vendor/_new/icon/account/personal.svg";
import { ReactComponent as EyeonIcon } from "../../../../vendor/_new/icon/account/eyeon.svg";
import { ReactComponent as EyeoffIcon } from "../../../../vendor/_new/icon/account/eyeoff.svg";
import { ReactComponent as KeyIcon } from "../../../../vendor/_new/icon/account/key.svg";
import { ReactComponent as ActionIcon } from "../../../../vendor/_new/icon/sites/action.svg";
import { ReactComponent as CopyIcon } from "../../../../vendor/_new/icon/sites/copy.svg";
import { ReactComponent as EditIcon } from "../../../../vendor/_new/icon/sites/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../../vendor/_new/icon/sites/delete.svg";
import { ReactComponent as PlusIcon } from "../../../../vendor/_new/icon/sites/plus.svg";
import { SafeTextField } from "../../../common/Greens";
import { formatDate, displayCell, getUserRoleString } from "../../../../utils/format";
import { UserType, normalizeUserType } from "../../../../utils/constants";

import useAuth from "../../../../hooks/useAuth";
import useKey from "../../../../hooks/user/useKey";
import { CancelButton, containedButtonSx, SaveButton } from "../../../common/Button";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../super/design";
import {
  validateProfileForm,
  validatePasswordChangeForm,
  validateApiKeyForm,
} from "../../../../utils/validation";

const tableHeadSx =  { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] };
const tableBodySx =  { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] };
const KeyPermissionMethods = ["Access full permission", "Customise permission"];

const getStatusLabel = (isDeleted) => {
  const label = isDeleted ? "Revoked" : "Active";
  let backgroundColor = label === "Active" ? colors.green[10] : colors.red[10];
  let borderColor = label === "Active" ? colors.green[100] : colors.red[100];
  let fontColor = label === "Active" ? colors.green[300] : colors.red[100];
  return (
    <Stack
      alignItems={"center"}
      justifyContent={"center"}
      sx={{ minWidth: 80, width: 80, height: 25, bgcolor: backgroundColor, borderRadius: "20px", border: `1px solid ${borderColor}` }}
    >
      <Typography sx={ { fontSize: 14, lineHeight: "21px", color: fontColor }}>{label}</Typography>
    </Stack>
  );
};

const Permissions = [
  {
    title: "Authentication & Access",
    items: [
      {
        title: "Users",
        description: "Manage users, roles, passwords",
        value: 0,
      },
      {
        title: "SSO",
        description: "Roles, Single Sign-On configuration",
        value: 11,
      },
      {
        title: "Auth",
        description: "Authentication settings (tokens, sessions, etc.)",
        value: 14,
      },
    ],
  },
  {
    title: "Web Properties",
    items: [
      {
        title: "Sites",
        description: "Add or manage domains/subdomains",
        value: 1,
      },
      {
        title: "SSL",
        description: "Manage certificates, TLS, HSTS",
        value: 4,
      },
      // {
      //   title: "DNS",
      //   description: "Could be added for domain routing",
      //   value: -1
      // },
    ],
  },
  {
    title: "Security & Protection",
    items: [
      {
        title: "WAF",
        description: "Web Application Firewall (general settings)",
        value: 2,
      },
      {
        title: "Rules",
        description: "Custom security rules",
        value: 3,
      },
      {
        title: "Firewall",
        description: "IP filtering, region blocking, advanced firewall features",
        value: 9,
      },
      {
        title: "Bot",
        description: "Bot detection and mitigation",
        value: 5,
      },
      {
        title: "Rate limit",
        description: "Request throttling and abuse prevention",
        value: 6,
      },
      {
        title: "DDos",
        description: "Distributed denial-of-service protection settings",
        value: 13,
      },
    ],
  },
  {
    title: "Monitoring & Visibility",
    items: [
      {
        title: "Logs",
        description: "Real-time or historical request logs",
        value: 7,
      },
      {
        title: "Statistics",
        description: "Traffic and threat analytics",
        value: 8,
      },
    ],
  },
  {
    title: "Communication & Alerts",
    items: [
      {
        title: "Notification",
        description: "Email, webhook, or system alerts related to activity or issues",
        value: 12,
      },
    ],
  },
  {
    title: "Account & Billing",
    items: [
      {
        title: "Payment",
        description: "Subscription, billing, and invoice management",
        value: 10,
      },
    ],
  },
  // {
  //   title: "API & Integration",
  //   items: [
  //     {
  //       title: "API Key Creation",
  //       description: "Create/manage keys for external access",
  //       value: -2
  //     },
  //   ],
  // },
];

function getApiKeyExpiresAt(item) {
  const raw = item?.expires_at || item?.expiresAt;
  if (raw) return raw;
  const created = item?.created_at || item?.createdAt;
  const days = Number(item?.duration);
  if (created && Number.isFinite(days) && days > 0) {
    return new Date(new Date(created).getTime() + days * ONE_DAY_IN_MS);
  }
  return null;
}

function previewApiKeyExpiry(item, durationDays) {
  const days = Number(durationDays);
  const ms = (Number.isFinite(days) ? days : 0) * ONE_DAY_IN_MS;
  const created = item?.created_at || item?.createdAt;
  if (created) {
    return new Date(new Date(created).getTime() + ms);
  }
  return new Date(Date.now() + ms);
}

const SyncFormValues = ({ values, onChange }) => {
  useEffect(() => {
    onChange(values);
  }, [values, onChange]);
  return null;
};

const NewAccountSettingsPage = () => {
  const { user, getUser, updateProfile, updateOrganisation } = useAuth();
  const { keys, getAllAPIKey, updateAPIKey, createAPIKey, setErr, errMsg } = useKey();
  const { showSuccess } = useNotification();
  const [selectedTab, setSelectedTab] = useState("profile");

  useEffect(() => {
    getAllAPIKey();
  }, [getAllAPIKey]);

  const getPermissionLabel = (id) => {
    const allItems = Permissions?.flatMap((p) => p.items || []);
    return allItems.find((item) => item.value === id)?.title || "";
  };

  const personalDetails = useMemo(() => {
    return {
      Organisation: user?.organisation?.title,
      Nodata: "",
      "First name": user?.firstName,
      "Last name": user?.lastName,
      Role: getUserRoleString(user?.role),
      Title: user?.title,
    };
  }, [user]);

  const [openEditPersonalData, setOpenEditPersonalData] = useState(false);
  const handleEditPersonalDataOpen = () => setOpenEditPersonalData(true);
  const handleEditPersonalDataClose = () => setOpenEditPersonalData(false);

  const [openChangePassword, setOpenChangePassword] = useState(false);
  const handleChangePasswordOpen = () => setOpenChangePassword(true);
  const handleChangePasswordClose = () => setOpenChangePassword(false);

  const [actionKeyMenu, setActionKeyMenu] = useState({
    anchorEl: null,
    keyId: null,
  });
  const handleActionKeyOpen = (event, keyId) => {
    event.stopPropagation();
    setActionKeyMenu({ anchorEl: event.currentTarget, keyId });
  };
  const handleActionKeyClose = () => {
    setActionKeyMenu({ anchorEl: null, keyId: null });
  };
  const isActionKeyMenuOpen = Boolean(actionKeyMenu.anchorEl);


  const [selectedKey, setSelectedKey] = useState(null);

  const [keyToDelete, setKeyToDelete] = useState(null);
  const [openKeyToDelete, setOpenKeyToDelete] = useState(false);
  const handleKeyToDeleteOpen = () => setOpenKeyToDelete(true);
  const handleKeyToDeleteClose = () => setOpenKeyToDelete(false);

  const [openEditKeyData, setOpenEditKeyData] = useState(false);
  const handleEditKeyDataOpen = () => setOpenEditKeyData(true);
  const handleEditKeyDataClose = () => setOpenEditKeyData(false);

  const [personalBaselineKey, setPersonalBaselineKey] = useState(0);
  const [personalFormValues, setPersonalFormValues] = useState(null);
  const [personalSubmitting, setPersonalSubmitting] = useState(false);

  const [passwordBaselineKey, setPasswordBaselineKey] = useState(0);
  const [passwordFormValues, setPasswordFormValues] = useState(null);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  const [keyBaselineKey, setKeyBaselineKey] = useState(0);
  const [keyFormValues, setKeyFormValues] = useState(null);
  const [keySubmitting, setKeySubmitting] = useState(false);

  const personalInitialValues = useMemo(
    () => ({
      organisation: user?.organisation?.title,
      firstName: user?.firstName,
      lastName: user?.lastName,
      title: user?.title,
      password: "",
      confirmPassword: "",
    }),
    [user],
  );

  useEffect(() => {
    if (openEditPersonalData) {
      setPersonalBaselineKey((k) => k + 1);
      setPersonalFormValues(personalInitialValues);
    }
  }, [openEditPersonalData, personalInitialValues]);

  const personalSnapshot = useMemo(
    () => personalFormValues ?? personalInitialValues,
    [personalFormValues, personalInitialValues],
  );
  const personalDirty = useDrawerBaselineDirty(openEditPersonalData, personalSnapshot, personalBaselineKey);
  const { requestClose: requestClosePersonal, confirmDialog: personalConfirmDialog } = useGuardedDrawerClose(
    handleEditPersonalDataClose,
    personalDirty,
    { disabled: personalSubmitting },
  );

  useEffect(() => {
    if (openChangePassword) {
      setPasswordBaselineKey((k) => k + 1);
      setPasswordFormValues({ password: "", confirmPassword: "" });
    }
  }, [openChangePassword]);

  const passwordSnapshot = useMemo(
    () => passwordFormValues ?? { password: "", confirmPassword: "" },
    [passwordFormValues],
  );
  const passwordDirty = useDrawerBaselineDirty(openChangePassword, passwordSnapshot, passwordBaselineKey);
  const { requestClose: requestClosePassword, confirmDialog: passwordConfirmDialog } = useGuardedDrawerClose(
    handleChangePasswordClose,
    passwordDirty,
    { disabled: passwordSubmitting },
  );

  useEffect(() => {
    if (openEditKeyData) {
      setKeyBaselineKey((k) => k + 1);
      setKeyFormValues(null);
    }
  }, [openEditKeyData, selectedKey]);

  const keyDirty = useDrawerBaselineDirty(openEditKeyData, keyFormValues ?? {}, keyBaselineKey);
  const { requestClose: requestCloseKey, confirmDialog: keyConfirmDialog } = useGuardedDrawerClose(
    handleEditKeyDataClose,
    keyDirty,
    { disabled: keySubmitting },
  );

  const [permissionMethod, setPermissionMethod] = useState(KeyPermissionMethods[1]);

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const handleClickShowPasswordConfirm = () => {
    setShowPasswordConfirm(!showPasswordConfirm);
  };

  const [deleting, setDeleting] = React.useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState(null);

  const currentKey = useMemo(() => {
    return keys?.find((key) => key._id === actionKeyMenu.keyId);
  }, [keys, actionKeyMenu]);

  return (
    <>
      <Helmet title="Account Management" />
      <Box sx={{ p: 6 }}>
        <Stack spacing={6}>
          <V2PageHeader title="Account Management" />
          <Tabs
            value={selectedTab}
            onChange={(_, v) => setSelectedTab(v)}
            sx={{
              minHeight: "inherit",
              height: 32,
              "& .MuiTabs-flexContainer": { border: "none", px: 0, py: 0, gap: "8px", height: 32 },
              "& .MuiTabs-indicator": { height: "1px", backgroundColor: colors.secondary[300] },
              "& button": { p: 0, minWidth: "inherit", minHeight: "inherit" },
              "& button .MuiTypography-root": { color: colors.neutral[700] },
              "& .Mui-selected .MuiTypography-root": { color: colors.neutral[1000] },
            }}
          >
            <Tab value={"profile"} label={<Typography sx={ { fontSize: 14 }}>Profile</Typography>} sx={{ width: 74 }} />
            <Tab value={"apikey"} label={<Typography sx={ { fontSize: 14 }}>API Keys</Typography>} sx={{ width: 90 }} />
          </Tabs>
          <Box>
            {selectedTab === "profile" && (
              <Stack spacing={6}>
                <Stack spacing={3}>
                  <Avatar
                    {...stringAvatar(user?.username)}
                    sx={{
                      bgcolor: colors.primary[500],
                      width: 82,
                      height: 82,
                      fontSize: 36,
                      fontWeight: "bold",
                      lineHeight: "54px",
                      cursor: "pointer",
                    }}
                  />
                  <Stack>
                    <Typography
                      sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      {user?.username}
                    </Typography>
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
                      {user?.email}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack spacing={4}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <Typography
                      sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Personal details
                    </Typography>
                    <Typography
                      onClick={handleEditPersonalDataOpen}
                      sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300], cursor: "pointer" }}
                    >
                      Edit
                    </Typography>
                  </Stack>
                  <Box sx={{ maxWidth: 800 }}>
                    <Grid container spacing={4}>
                      {Object.keys(personalDetails)?.map((key, index) => (
                        <Grid
                          key={`personal_details_${index}`}
                          item
                          xs={12}
                          sm={6}
                          sx={{ display: key === "Nodata" ? { xs: "none", md: "inherit" } : "inherit" }}
                        >
                          <Stack
                            direction={"row"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                            spacing={2}
                            sx={{
                              borderRadius: "8px",
                              border: `solid 1px ${key === "Nodata" ? "transparent" : colors.neutral[200]}`,
                              px: 6,
                              py: 4,
                              width: "100%",
                            }}
                          >
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600] }}>
                              {key === "Nodata" ? "" : key}
                            </Typography>
                            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                              {key === "Nodata" ? "" : personalDetails[key] || "N/A"}
                            </Typography>
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Stack>
                <Stack spacing={4}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <Typography
                      sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Change password
                    </Typography>
                    <Typography
                      onClick={handleChangePasswordOpen}
                      sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300], cursor: "pointer" }}
                    >
                      Change
                    </Typography>
                  </Stack>
                  <Box sx={{ maxWidth: 800 }}>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={6}>
                        <Stack
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          sx={{
                            borderRadius: "8px",
                            border: `solid 1px ${colors.neutral[200]}`,
                            px: 6,
                            py: 4,
                            width: "100%",
                          }}
                        >
                          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600] }}>
                            Password
                          </Typography>
                          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                            **********************
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              </Stack>
            )}
            {selectedTab === "apikey" && (
              <Stack spacing={6}>
                <Button
                  startIcon={<PlusIcon style={{ color: colors.neutral[100] }} />}
                  onClick={() => {
                    setSelectedKey(null);
                    handleEditKeyDataOpen();
                  }}
                  sx={{ height: 32, width: 126, ...containedButtonSx }}
                >
                  Add API key
                </Button>
                <Box spacing={6} sx={{ border: `solid 1px ${colors.neutral[200]}`, borderRadius: "8px" }}>
                  <Table
                    sx={{
                      p: 4,
                      "& .MuiTableCell-head": { py: 3, px: 3 },
                      "& .MuiTableCell-body": { py: 2, px: 3 },
                      "& .MuiTableCell-root": { borderBottom: "none" },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell width={"15%"}>
                          <Typography sx={tableHeadSx}>Name</Typography>
                        </TableCell>
                        <TableCell width={"15%"}>
                          <Typography sx={tableHeadSx}>Value</Typography>
                        </TableCell>
                        <TableCell width={"40%"}>
                          <Typography sx={tableHeadSx}>Permission</Typography>
                        </TableCell>
                        <TableCell width={"10%"}>
                          <Typography sx={tableHeadSx}>Status</Typography>
                        </TableCell>
                        <TableCell width={"15%"}>
                          <Typography sx={tableHeadSx}>Expires at</Typography>
                        </TableCell>
                        <TableCell width={"5%"}>
                          <Typography sx={tableHeadSx}>Action</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {keys?.map((item, index) => (
                        <TableRow index={`key_${index}`}>
                          <TableCell>
                            <Typography sx={tableBodySx}>{displayCell(item.name)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction={"row"} alignItems={"center"} spacing={3}>
                              <Typography sx={tableBodySx}>
                                {item?.key
                                  ? item.key.substr(0, 4) + "*********" + item.key.substr(-4)
                                  : displayCell(item?.key)}
                              </Typography>
                              <Tooltip
                                title={copiedKeyId === (item._id || item.id || `row-${index}`) ? "Copied!" : "Click to Copy"}
                                leaveDelay={copiedKeyId === (item._id || item.id || `row-${index}`) ? 500 : 200}
                              >
                              <Box
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!item?.key) return;
                                  const rowKey = item._id || item.id || `row-${index}`;
                                  copy(item.key);
                                  setCopiedKeyId(rowKey);
                                  setTimeout(() => setCopiedKeyId((id) => (id === rowKey ? null : id)), 2000);
                                }}
                                sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                              >
                                <CopyIcon style={{ color: colors.neutral[600] }} />
                              </Box>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ width: "100%", wordBreak: "break-word" }}>
                              <Typography sx={tableBodySx}>
                                {displayCell(
                                  (item?.permissions || []).map((permission) => getPermissionLabel(permission)).join(", ")
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{getStatusLabel(item.status)}</TableCell>
                          <TableCell>
                            <Typography sx={tableBodySx}>{displayCell(formatDate(getApiKeyExpiresAt(item)))}</Typography>
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              justifyContent={"center"}
                              onClick={(e) => handleActionKeyOpen(e, item._id, item.status === 1)}
                              sx={{ cursor: "pointer", width: 24, height: 24 }}
                            >
                              <ActionIcon style={{ color: colors.neutral[600] }} />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                      {keys?.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9}>
                            <Typography sx={tableBodySx} textAlign="center">
                              There are no API keys
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>
      <Modal open={openEditPersonalData} onClose={requestClosePersonal}>
        <Slide direction="left" in={openEditPersonalData} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
            }}
          >
            <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4 }}>
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                  }}
                >
                  <PersonalIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                </Box>
                <Stack>
                  <Typography
                    sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                  >
                    Personal details
                  </Typography>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Review and modify your profile.
                  </Typography>
                </Stack>
              </Stack>
              <Box onClick={requestClosePersonal} sx={{ cursor: "pointer" }}>
                <CloseIcon sx={{ color: colors.neutral[600] }} />
              </Box>
            </Stack>

            <Formik
              enableReinitialize
              validateOnMount
              validateOnChange
              initialValues={{
                organisation: user?.organisation?.title,
                firstName: user?.firstName,
                lastName: user?.lastName,
                title: user?.title,
                // oldPassword: "",
                password: "",
                confirmPassword: "",
              }}
              validationSchema={Yup.object().shape({
                organisation: Yup.string().max(255).required("Required"),
                title: Yup.string().max(255),
                firstName: Yup.string().max(255).required("First Name is required"),
                lastName: Yup.string().max(255).required("Last Name is required"),
                password: Yup.string().min(8, "Must be at least 8 characters").max(255),
                confirmPassword: Yup.string().when("password", {
                  is: (val) => (val && val.length > 0 ? true : false),
                  then: Yup.string().oneOf([Yup.ref("password")], "Both password need to be the same"),
                }),
              })}
              validate={(values) => {
                const result = validateProfileForm(values);
                const errors = { ...result.errors };
                if (values.password || values.confirmPassword) {
                  const pw = validatePasswordChangeForm(values);
                  if (!pw.valid) Object.assign(errors, pw.errors);
                }
                return errors;
              }}
              onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting, setValue }) => {
                setPersonalSubmitting(true);
                try {
                  const orgValues = { title: values.organisation };
                  await updateOrganisation(orgValues);
                  await updateProfile(values);
                  await getUser();
                  handleEditPersonalDataClose();
                } catch (error) {
                  const message = error.message || "Something went wrong";
                  setStatus({ success: false });
                  setErrors({ submit: message });
                  setSubmitting(false);
                } finally {
                  setPersonalSubmitting(false);
                }
              }}
            >
              {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, isValid, touched, values }) => (
                <>
                  <SyncFormValues values={values} onChange={setPersonalFormValues} />
                <form noValidate onSubmit={handleSubmit}>
                  <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 104px)` }}>
                    <ReactPerfectScrollbar>
                      <Stack spacing={4} justifyContent={"space-between"} sx={{ pr: 4, height: "100%" }}>
                        <Grid container spacing={4}>
                          {errors.submit && (
                            <Grid item xs={12}>
                              <Alert variant="outlined" severity="error">
                                {errors.submit}
                              </Alert>
                            </Grid>
                          )}
                          <Grid item xs display="flex">
                            <SafeTextField
                              label="Organisation"
                              type="organisation"
                              name="organisation"
                              fullWidth
                              value={values.organisation}
                              error={Boolean(touched.organisation && errors.organisation)}
                              helperText={touched.organisation && errors.organisation}
                              onBlur={handleBlur}
                              onChange={(e) => {
                                if (normalizeUserType(user?.role) === UserType.ORG_USER) return;
                                handleChange(e);
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <SafeTextField
                              label="First Name"
                              name="firstName"
                              value={values.firstName}
                              error={Boolean(touched.firstName && errors.firstName)}
                              fullWidth
                              helperText={touched.firstName && errors.firstName}
                              onBlur={handleBlur}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <SafeTextField
                              label="Last Name"
                              name="lastName"
                              value={values.lastName}
                              error={Boolean(touched.lastName && errors.lastName)}
                              fullWidth
                              helperText={touched.lastName && errors.lastName}
                              onBlur={handleBlur}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <SafeTextField
                              label="Role"
                              name="role"
                              value={getUserRoleString(user?.role)}
                              error={Boolean(touched.role && errors.role)}
                              fullWidth
                              helperText={touched.role && errors.role}
                              onBlur={handleBlur}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <SafeTextField
                              label="Title"
                              name="title"
                              value={values.title}
                              error={Boolean(touched.title && errors.title)}
                              fullWidth
                              helperText={touched.title && errors.title}
                              onBlur={handleBlur}
                              onChange={handleChange}
                            />
                          </Grid>
                        </Grid>
                        <Stack direction={"row"} alignItems={"center"} spacing={3}>
                          <Button type="submit" disabled={isSubmitting || !isValid} sx={{ height: 44, width: "50%", ...containedButtonSx }}>
                            Save
                          </Button>
                          <CancelButton onClick={requestClosePersonal} />
                        </Stack>
                      </Stack>
                    </ReactPerfectScrollbar>
                  </Box>
                </form>
                </>
              )}
            </Formik>
          </Stack>
        </Slide>
      </Modal>
      {personalConfirmDialog}
      <Modal open={openChangePassword} onClose={requestClosePassword}>
        <Slide direction="left" in={openChangePassword} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "absolute",
              top: 20,
              right: 20,
              height: `calc(100vh - 40px)`,
              width: 430,
              backgroundColor: colors.neutral[100],
              borderRadius: "8px",
            }}
          >
            <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4 }}>
              <Stack>
                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                  <PersonalIcon style={{ color: colors.neutral[1000] }} />
                  <Typography
                    sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                  >
                    Change password
                  </Typography>
                </Stack>
              </Stack>
              <Box onClick={requestClosePassword} sx={{ cursor: "pointer" }}>
                <CloseIcon sx={{ color: colors.neutral[600] }} />
              </Box>
            </Stack>
            <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700], pl: 11 }}>
              Enter your new password and confirm to proceed.
            </Typography>
            <Formik
              enableReinitialize
              validateOnMount
              validateOnChange
              initialValues={{
                password: "",
                confirmPassword: "",
              }}
              validate={(values) => validatePasswordChangeForm(values).errors}
              validationSchema={Yup.object().shape({
                password: Yup.string().required("Password is required").min(8, "Must be at least 8 characters").max(255),
                confirmPassword: Yup.string()
                  .required("Please confirm your password")
                  .oneOf([Yup.ref("password")], "Both password need to be the same"),
              })}
              onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
                setPasswordSubmitting(true);
                try {
                  const response = await updateProfile(values);
                  await getUser();
                  if (response) {
                    resetForm();
                    setErrors({ success: "Password has been changed successfully." });
                  }
                } catch (error) {
                  const message = error.message || "Something went wrong";
                  setStatus({ success: false });
                  setErrors({ submit: message });
                  setSubmitting(false);
                } finally {
                  setPasswordSubmitting(false);
                }
              }}
            >
              {({ errors, handleBlur, handleChange, handleSubmit, resetForm, isSubmitting, isValid, touched, values }) => (
                <>
                  <SyncFormValues values={values} onChange={setPasswordFormValues} />
                <form noValidate onSubmit={handleSubmit}>
                  <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 104px)` }}>
                    <ReactPerfectScrollbar>
                      <Stack justifyContent={"space-between"} spacing={4} sx={{ pr: 4, height: "100%" }}>
                        <Stack spacing={4} width={"100%"}>
                          {errors.submit && (
                            <Alert mt={2} variant="outlined" severity="error">
                              {errors.submit}
                            </Alert>
                          )}
                          {errors.success && (
                            <Alert mt={2} variant="outlined" severity="info">
                              {errors.success}
                            </Alert>
                          )}
                          <SafeTextField
                            label={"New password"}
                            fullWidth
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={values.password}
                            error={Boolean(touched.password && errors.password)}
                            helperText={touched.password && errors.password}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Box onClick={handleClickShowPassword} sx={{ cursor: "pointer" }}>
                                    {showPassword ? (
                                      <EyeonIcon style={{ color: colors.neutral[600] }} />
                                    ) : (
                                      <EyeoffIcon style={{ color: colors.neutral[600] }} />
                                    )}
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <SafeTextField
                            label={"Confirm new password"}
                            fullWidth
                            type={showPasswordConfirm ? "text" : "password"}
                            name="confirmPassword"
                            value={values.confirmPassword}
                            error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                            helperText={touched.confirmPassword && errors.confirmPassword}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Box onClick={handleClickShowPasswordConfirm} sx={{ cursor: "pointer" }}>
                                    {showPasswordConfirm ? (
                                      <EyeonIcon style={{ color: colors.neutral[600] }} />
                                    ) : (
                                      <EyeoffIcon style={{ color: colors.neutral[600] }} />
                                    )}
                                  </Box>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Stack>
                        <Stack direction={"row"} alignItems={"center"} spacing={3}>
                          <Button type="submit" disabled={isSubmitting || !isValid} sx={{ height: 44, width: "50%", ...containedButtonSx }}>
                            Save
                          </Button>
                          <CancelButton onClick={requestClosePassword} />
                        </Stack>
                      </Stack>
                    </ReactPerfectScrollbar>
                  </Box>
                </form>
                </>
              )}
            </Formik>
          </Stack>
        </Slide>
      </Modal>
      {passwordConfirmDialog}
      <Menu
        anchorEl={actionKeyMenu.anchorEl}
        open={isActionKeyMenuOpen}
        onClose={handleActionKeyClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            // border: `solid 1px ${colors.neutral[200]}`,
            width: 150,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { keyId } = actionKeyMenu;
              copy(keys?.find((key) => key?._id === keyId)?.key);
              showSuccess("Copied!");
              handleActionKeyClose();
            }}
            sx={generateMenuSx(126, 37)}
          >
            <CopyIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>Copy</Typography>
          </Stack>
          {currentKey?.status !== 1 && (
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={1}
              onClick={() => {
                const { keyId } = actionKeyMenu;
                setSelectedKey(keys.find((key) => key._id === keyId));
                handleEditKeyDataOpen();
                handleActionKeyClose();
              }}
              sx={generateMenuSx(126, 37)}
            >
              <EditIcon />
              <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                Edit
              </Typography>
            </Stack>
          )}
          {currentKey?.status !== 1 && (
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={1}
              onClick={() => {
                const { keyId } = actionKeyMenu;
                setKeyToDelete(keys.find((key) => key._id === keyId));
                handleKeyToDeleteOpen();
                handleActionKeyClose();
              }}
              sx={generateMenuSx(126, 37)}
            >
              <DeleteIcon />
              <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
                Delete
              </Typography>
            </Stack>
          )}
        </Box>
      </Menu>
      <Modal open={openKeyToDelete} onClose={handleKeyToDeleteClose}>
        <Stack
          spacing={4}
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: colors.neutral[100],
            borderRadius: "8px",
            p: 6,
          }}
        >
          <Stack spacing={3} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              Delete API Key?
            </Typography>
            <Box onClick={handleKeyToDeleteClose} sx={{ cursor: "pointer" }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </Box>
          </Stack>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            Are you sure you want to delete this{" "}
            <span style={{ color: colors.neutral[1000] }}>
              <b>{keyToDelete?.name}</b>
            </span>{" "}
            API key? <br /> This action will immediately revoke access for any systems using this key.
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={4}>
            <CancelButton onClick={handleKeyToDeleteClose} outlined={true} />
            <SaveButton
              onClick={async () => {
                setDeleting(true);
                await updateAPIKey(keyToDelete?._id, { status: 1 });
                setDeleting(false);
                handleKeyToDeleteClose();
              }}
              disabled={deleting}
              label="Delete"
            />
          </Stack>
        </Stack>
      </Modal>
      <Modal open={openEditKeyData} onClose={requestCloseKey}>
        <Slide direction="left" in={openEditKeyData} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              height: "100vh",
              minWidth: 480,
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
            }}
          >
            <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4 }}>
              <Stack direction={"row"} alignItems={"center"} spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                  }}
                >
                  <KeyIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                </Box>
                <Stack>
                  <Typography
                    sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                  >
                    {!selectedKey ? "Add API Key" : "Edit API Key"}
                  </Typography>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Use API keys to authenticate and manage access securely.
                  </Typography>
                </Stack>
              </Stack>
              <Box onClick={requestCloseKey} sx={{ cursor: "pointer" }}>
                <CloseIcon sx={{ color: colors.neutral[600] }} />
              </Box>
            </Stack>
            <Formik
              enableReinitialize={true}
              validateOnMount
              validateOnChange
              initialValues={{
                name: selectedKey?.name || "",
                duration: selectedKey?.duration || 7,
                permissions: selectedKey?.permissions || [],
              }}
              validate={(values) => validateApiKeyForm(values).errors}
              validationSchema={Yup.object().shape({
                name: Yup.string().required("Name is required"),
                duration: Yup.number().integer("Must be an integer").required("Duration is required"),
                permissions: Yup.array().min(1, "At least 1 permission is required"),
              })}
              onSubmit={async (values, { setSubmitting }) => {
                setKeySubmitting(true);
                setSubmitting(true);
                try {
                  if (!!selectedKey) {
                    await updateAPIKey(selectedKey._id, values);
                  } else {
                    await createAPIKey(values);
                  }
                  handleEditKeyDataClose();
                } catch (err) {
                  setErr(err?.message);
                }
                setSubmitting(false);
                setKeySubmitting(false);
              }}
            >
              {({ values, touched, errors, isSubmitting, isValid, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
                <>
                  <SyncFormValues values={values} onChange={setKeyFormValues} />
                <form noValidate onSubmit={handleSubmit}>
                  <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 104px)` }}>
                    <ReactPerfectScrollbar>
                      <Stack spacing={4} justifyContent={"space-between"} sx={{ pr: 4, height: "100%" }}>
                        {errMsg && (
                          <Alert variant="outlined" color="error">
                            {errMsg}
                          </Alert>
                        )}
                        <Stack spacing={4} width={"100%"}>
                          <SafeTextField
                            fullWidth
                            label={"Name"}
                            name="name"
                            value={values.name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(touched.name && errors.name)}
                            helperText={touched.name && errors.name}
                          />
                          <SafeTextField
                            fullWidth
                            label={"Duration (Days)"}
                            type="number"
                            name="duration"
                            value={values.duration}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            error={Boolean(touched.duration && errors.duration)}
                            helperText={touched.duration && errors.duration}
                          />
                          <SafeTextField
                            fullWidth
                            label={"Your API key will be expired at"}
                            value={displayCell(formatDate(previewApiKeyExpiry(selectedKey, values.duration)))}
                          />
                          <RadioGroup
                            value={permissionMethod}
                            onChange={(e) => {
                              setPermissionMethod(e.target.value);
                              let tmpArr = [];
                              if (e.target.value === "Access full permission") {
                                const allItems = Permissions?.flatMap((p) => p.items || []);
                                tmpArr = allItems?.map((item) => item?.value);
                              }
                              setFieldValue("permissions", tmpArr);
                            }}
                          >
                            {KeyPermissionMethods.map((label, index) => (
                              <FormControlLabel
                                kye={`permission_method_${index}`}
                                value={label}
                                control={
                                  <Radio
                                    size="small"
                                    color="default"
                                    sx={{
                                      color: colors.neutral[300],
                                      "&.Mui-checked": {
                                        color: colors.primary[300],
                                      },
                                    }}
                                  />
                                }
                                label={label}
                                sx={{
                                  height: 37,
                                  "& .MuiFormControlLabel-label": {
                                    fontSize: 14,
                                    lineHeight: "21px",
                                    color: colors.neutral[800],
                                    "&:hover": { color: colors.primary[300] },
                                  },
                                }}
                              />
                            ))}
                          </RadioGroup>
                          {Permissions?.map((permission, index) => (
                            <Stack key={`permission_${index}`} spacing={2}>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  lineHeight: "18px",
                                  fontWeight: "medium",
                                  color: colors.neutral[1000],
                                }}
                              >
                                {permission.title}
                              </Typography>
                              {permission.items?.map((item, index2) => (
                                <Stack key={`permission_item_${index}_${index2}`}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={values.permissions.indexOf(item.value) !== -1}
                                        onChange={(e) => {
                                          let tmpArr = [...values.permissions];
                                          if (e.target.checked) {
                                            tmpArr.push(item.value);
                                          } else {
                                            let id = tmpArr.indexOf(item.value);
                                            if (id > -1) tmpArr.splice(id, 1);
                                          }

                                          const allItems = Permissions?.flatMap((p) => p.items || []);
                                          if (allItems?.length !== tmpArr?.length) {
                                            setPermissionMethod(KeyPermissionMethods[1]);
                                          } else {
                                            setPermissionMethod(KeyPermissionMethods[0]);
                                          }
                                          setFieldValue("permissions", tmpArr);
                                        }}
                                        size="small"
                                        color="default"
                                        sx={{
                                          color: colors.neutral[300],
                                          "&.Mui-checked": {
                                            color: colors.primary[300],
                                          },
                                        }}
                                      />
                                    }
                                    label={item.title}
                                    sx={{
                                      height: 21,
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: 14,
                                        lineHeight: "21px",
                                        fontWeight: "medium",
                                        "&:hover": { color: colors.primary[300] },
                                      },
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      lineHeight: "18px",
                                      color: colors.neutral[700],
                                      pl: 10,
                                    }}
                                  >
                                    {item.description}
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          ))}
                          {errors.permissions && <FormHelperText error>{errors.permissions}</FormHelperText>}
                        </Stack>
                        <Stack direction={"row"} alignItems={"center"} spacing={3}>
                          <Button type="submit" disabled={isSubmitting || !isValid} sx={{ height: 44, width: "50%", ...containedButtonSx }}>
                            Save
                          </Button>
                          <CancelButton onClick={requestCloseKey} />
                        </Stack>
                      </Stack>
                    </ReactPerfectScrollbar>
                  </Box>
                </form>
                </>
              )}
            </Formik>
          </Stack>
        </Slide>
      </Modal>
      {keyConfirmDialog}
    </>
  );
};
export default NewAccountSettingsPage;
